import { useCallback, useEffect, useRef, useState } from 'react'
import { useLazyGetAccountOpeningQuery } from '../../../store/accountOpening/accountOpening'
import { type IntegrationServiceId } from '../../../common/integrations'
import { type AccountOpeningDTO } from '../../../models/AccountOpening'
import { WIZARD_ERRORS } from '../descriptor/stepCopy'
import { type ResolvedCatalog } from './useAccountTypeCatalog'
import { type LoadOpeningOptions } from './useAccountOpeningPersistence'

interface ResumeOpeningOptions {
  accountId: string
  catalog: ResolvedCatalog
  loadOpening: (dto: AccountOpeningDTO, options: LoadOpeningOptions) => void
  setConnector: (connector: IntegrationServiceId) => void
}

interface ResumeOpening {
  resume: (openingId: string, asReadOnly: boolean) => Promise<void>
  busyOpeningId: string | null
  error: string | null
  reset: () => void
}

export const useResumeOpening = ({ accountId, catalog, loadOpening, setConnector }: ResumeOpeningOptions): ResumeOpening => {
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const [fetchFailed, setFetchFailed] = useState(false)
  const [pending, setPending] = useState<{ dto: AccountOpeningDTO; readOnly: boolean } | null>(null)
  const [triggerGetOpening] = useLazyGetAccountOpeningQuery()

  const resume = useCallback(
    async (openingId: string, asReadOnly: boolean) => {
      setRequestedId(openingId)
      setFetchFailed(false)
      setPending(null)
      try {
        const response = await triggerGetOpening({ accountId, openingId }).unwrap()
        setConnector(response.data.connector as IntegrationServiceId)
        setPending({ dto: response.data, readOnly: asReadOnly })
      } catch {
        setFetchFailed(true)
      }
    },
    [accountId, triggerGetOpening, setConnector],
  )

  const catalogFailed = catalog.descriptorFailed || catalog.accountTypesFailed || catalog.selectorsFailed
  const error = fetchFailed || (pending && catalogFailed) ? WIZARD_ERRORS.resume : null

  const decodedId = useRef<string | null>(null)
  useEffect(() => {
    if (!pending || decodedId.current === pending.dto.id || catalogFailed) {
      return
    }
    if (catalog.descriptor?.connector !== pending.dto.connector || catalog.isLoadingAccountTypes) {
      return
    }
    decodedId.current = pending.dto.id
    loadOpening(pending.dto, { readOnly: pending.readOnly })
  }, [pending, catalog, catalogFailed, loadOpening])

  const busyOpeningId = error ? null : requestedId

  const reset = useCallback(() => {
    setFetchFailed(false)
    setPending(null)
    setRequestedId(null)
  }, [])

  return { resume, busyOpeningId, error, reset }
}
